import React, { useMemo } from "react";
import { Link } from "react-router-dom";
import { Settings as SettingsIcon } from "lucide-react";
import { useRangeQuery } from "@/lib/useData";
import { useDateRange } from "@/lib/dateRange";
import { useUrlFilters } from "@/lib/useUrlFilters";
import { FilterBar } from "@/components/shared/FilterBar";
import { LoadingBlocks, ErrorState, Forbidden } from "@/components/shared/primitives";
import { DataTable, EmptyState } from "@/components/kit";
import { relativeLastActive } from "@/lib/format";

const LEVEL = {
  Regular: { dot: "var(--brand-kawaii)", label: "Regular" },
  Occasional: { dot: "var(--brand-floral)", label: "Occasional" },
  "Not yet": { dot: "var(--text-muted)", label: "Not yet started" },
};

function UsageBadge({ level }) {
  const l = LEVEL[level] || LEVEL["Not yet"];
  return (
    <span className="inline-flex items-center gap-1.5 rounded-md px-2 py-1 text-[13px] font-semibold text-nb-ink" style={{ background: "var(--bg-sunken)" }}>
      <span className="w-2 h-2 rounded-full" style={{ background: l.dot }} />{l.label}
    </span>
  );
}

export function MembersPanel() {
  const { search } = useDateRange();
  const { values, setFilter, clearAll } = useUrlFilters(["team", "department", "usage", "seniority", "location", "sort"]);
  const { data, isLoading, error, refetch } = useRangeQuery("members", "/members", { search: search || undefined });

  const allRows = useMemo(() => data?.data || [], [data]);
  const opt = (k) => [...new Set(allRows.map((m) => m[k]).filter(Boolean))].sort().map((v) => ({ value: v, label: v }));
  const teamOptions = useMemo(() => [...new Set(allRows.map((m) => m.team).filter(Boolean))].sort().map((n) => ({ value: n, label: n })), [allRows]);
  const hasSeniority = allRows.some((m) => m.seniority);
  const hasOffice = allRows.some((m) => m.location);
  const hasDept = allRows.some((m) => m.department);

  const rows = useMemo(() => {
    let r = [...allRows];
    if (values.department) r = r.filter((m) => m.department === values.department);
    if (values.team) r = r.filter((m) => m.team === values.team);
    if (values.usage) r = r.filter((m) => m.usage_level === values.usage);
    if (values.seniority) r = r.filter((m) => m.seniority === values.seniority);
    if (values.location) r = r.filter((m) => m.location === values.location);
    const USAGE_ORDER = { Regular: 0, Occasional: 1, "Not yet": 2 };
    const sort = values.sort || "name";
    const cmp = {
      name: (a, b) => a.name.localeCompare(b.name),
      department: (a, b) => (a.department || "").localeCompare(b.department || "") || a.name.localeCompare(b.name),
      team: (a, b) => (a.team || "").localeCompare(b.team || "") || a.name.localeCompare(b.name),
      location: (a, b) => (a.location || "").localeCompare(b.location || "") || a.name.localeCompare(b.name),
      usage: (a, b) => (USAGE_ORDER[a.usage_level] ?? 3) - (USAGE_ORDER[b.usage_level] ?? 3) || a.name.localeCompare(b.name),
    }[sort] || ((a, b) => a.name.localeCompare(b.name));
    return r.sort(cmp);
  }, [allRows, values.department, values.team, values.usage, values.seniority, values.location, values.sort]);

  if (error?.code === "forbidden") return <Forbidden who="Admins (and Partners when enabled)" />;

  const active = search || values.department || values.team || values.usage || values.seniority || values.location;
  const columns = [
    { key: "name", label: "Name", always: true, render: (m) => <Link to={`/members/${m.member_id}`} className="hover:text-[var(--text-link)]">{m.name}</Link> },
    { key: "department", label: "Department", render: (m) => m.department || "-" },
    { key: "team", label: "Team", render: (m) => m.team ? <Link to={`/teams/${m.team_id}`} className="hover:text-[var(--text-link)] font-normal">{m.team}</Link> : "-" },
    { key: "seniority", label: "Seniority" },
    { key: "location", label: "Office" },
    { key: "tools_assigned", label: "Tools assigned", numeric: true },
    { key: "tools_used", label: "Tools used", numeric: true },
    { key: "last_active", label: "Last active", render: (m) => <span className="font-normal text-nb-ink2">{relativeLastActive(m.last_active)}</span> },
    { key: "usage_level", label: "Usage level", render: (m) => <UsageBadge level={m.usage_level} /> },
  ];

  return (
    <div>
      {data && !hasSeniority && !hasOffice && (
        <div className="rounded-[10px] px-4 py-3 mb-4 text-[14px] text-nb-ink" style={{ background: "var(--brand-floral)" }} data-testid="people-banner">
          Add seniority and office in <Link to="/settings" className="font-semibold underline">Settings</Link> to filter by them.
        </div>
      )}
      {allRows.length > 0 && (
        <FilterBar
          values={values} onChange={setFilter} onClear={clearAll}
          filters={[
            ...(hasDept ? [{ key: "department", label: "Department", placeholder: "All departments", options: opt("department") }] : []),
            { key: "team", label: "Team", placeholder: "All teams", options: teamOptions },
            ...(hasSeniority ? [{ key: "seniority", label: "Seniority", placeholder: "All seniority", options: opt("seniority") }] : []),
            ...(hasOffice ? [{ key: "location", label: "Office", placeholder: "All offices", options: opt("location") }] : []),
            { key: "usage", label: "Usage level", placeholder: "All usage levels", options: [
              { value: "Regular", label: "Regular" }, { value: "Occasional", label: "Occasional" }, { value: "Not yet", label: "Not yet started" },
            ] },
          ]}
          sort={{ key: "sort", label: "Sort by", options: [
            { value: "name", label: "Name (A–Z)" },
            { value: "department", label: "Department" },
            { value: "team", label: "Team" },
            { value: "location", label: "Office" },
            { value: "usage", label: "Usage level" },
          ] }}
        />
      )}

      {isLoading && <LoadingBlocks rows={5} />}
      {error && error.code !== "forbidden" && <ErrorState error={error} onRetry={refetch} />}
      {data && rows.length === 0 && (
        <EmptyState title={active ? "No people match those filters." : "No people yet."} body={active ? "Try clearing a filter." : "Add people in Settings, under Teams and people."} />
      )}
      {rows.length > 0 && (
        <div data-testid="members-table">
          <DataTable columns={columns} rows={rows} getRowKey={(m) => m.member_id} onRowClick={null} testid="members-data-table" />
        </div>
      )}
    </div>
  );
}
